/**
 * Lumyn Live — active session control panel (host).
 *
 * Extracted verbatim from LiveHostPage.jsx during the Phase 2 refactor
 * (no behaviour change).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../../../hooks/useApi';
import { faviconLoader } from '../../../utils/faviconLoader';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { LumynBadge } from '../../common/LumynBadge';
import {
  Flame, Play, ChevronRight, ChevronLeft, Trophy, Trash2, Users, Mail, MailX,
  ExternalLink, Download, FileText, FileSpreadsheet,
  Cast, Minimize2, Pencil, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import UpNextPreview from '../UpNextPreview';
import { formatSessionDateTime, bestSessionTimestamp } from '../../../utils/formatSessionDateTime';
import { useProjectorMode } from '../../../hooks/useProjectorMode';
import { CastProgressBar } from './CastProgressBar';
import { IntegritySummaryCard } from './IntegritySummaryCard';
import { DistributionBars } from './DistributionBars';
import { RenamePopover } from './RenamePopover';
import { ParticipantModerationPanel } from './ParticipantModerationPanel';
import { Lock, LockOpen } from 'lucide-react';

export function HostControl({ session, wsConnected, onUpdated, onClose }) {
  const { post, del, get } = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to');
  const [busy, setBusy] = useState(false);
  const [headerRenaming, setHeaderRenaming] = useState(false);
  const projector = useProjectorMode();

  // Real-time capacity meter — polls every 15 s while the session is live.
  // Surfaces whether any participant is being rate-limited (e.g. very chatty
  // client, mis-configured proxy). Trainers spot trouble before participants
  // start complaining.
  const [health, setHealth] = useState(null);
  useEffect(() => {
    if (!session?.session_id) return undefined;
    if (session.status === 'ended') return undefined;
    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const res = await get(`/live/sessions/${session.session_id}/health`);
        if (!cancelled) setHealth(res);
      } catch {
        /* silent — health chip just stays at last value */
      }
    };
    fetchHealth();
    const id = setInterval(fetchHealth, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [session?.session_id, session?.status, get]);

  const wrap = async (path) => {
    setBusy(true);
    // Only the initial 'start' kicks off the room, websocket bootstrap, and
    // first slide push — that's the operation worth a "we're working" cue in
    // the tab. The other lifecycle calls (advance/previous/end) return in
    // milliseconds and don't need an animated favicon.
    const releaseFavicon = path === 'start' ? faviconLoader.start() : null;
    try {
      const res = await post(`/live/sessions/${session.session_id}/${path}`);
      onUpdated(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || `${path} failed`);
    } finally {
      releaseFavicon?.();
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this session and all answers?')) return;
    try {
      await del(`/live/sessions/${session.session_id}`);
      toast.success('Session deleted');
      onClose();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleRevoke = async () => {
    // Kill-switch: hard-end the session AND mark it revoked so participants
    // see "host ended the session" instead of the normal end-of-deck screen.
    // We use a single prompt for confirmation + optional reason to keep the
    // host flow one-click-from-decision (panicked trainers don't want a modal).
    const reason = window.prompt(
      'Revoke this session immediately?\n\n'
      + 'All participants will be disconnected and the ref code will stop working.\n\n'
      + 'Optional: enter a short reason participants will see (e.g. "Wrong slide deck"). '
      + 'Leave blank to skip.',
      '',
    );
    if (reason === null) return;  // user hit Cancel
    setBusy(true);
    try {
      const res = await post(
        `/live/sessions/${session.session_id}/revoke`,
        { reason: reason.trim() || undefined },
      );
      onUpdated(res);
      toast.success('Session revoked — participants notified');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revoke failed');
    } finally {
      setBusy(false);
    }
  };

  const isRunning = session.status === 'running';
  const isLobby = session.status === 'lobby';
  const isEnded = session.status === 'ended';
  const currentQ = (session.question_versions && session.question_versions[session.current_question_index]) || null;
  const currentDist = (session.question_distributions || [])
    .find((d) => d.question_index === session.current_question_index);
  const allDist = session.question_distributions || [];
  const joinUrl = `${window.location.origin}/live/${session.ref_code}`;

  // Phase 3 — reports for ended sessions
  // Use SAME-ORIGIN relative URLs so the link works regardless of which
  // REACT_APP_BACKEND_URL the SPA was built against (prod vs preview).
  // The Kubernetes ingress routes /api/* → backend on every environment.
  const reportPdfUrl = `/api/v1/live/sessions/${session.session_id}/report.pdf`;
  const reportXlsxUrl = `/api/v1/live/sessions/${session.session_id}/report.xlsx`;

  return (
    <div
      className="dark bg-slate-950 text-slate-50 -mx-6 -my-6 sm:-mx-6 px-6 py-6 min-h-[calc(100vh-var(--topbar-height))] relative overflow-hidden"
      data-testid="host-control"
    >
      {/* Mission-Control ambient: dual orange glow + dot grid */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-30 bg-[var(--lumyn-live)]" />
        <div className="absolute top-1/3 -right-40 w-[420px] h-[420px] rounded-full blur-3xl opacity-20 bg-[var(--lumyn-brand)]" />
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />
      </div>

      <div className="relative z-10 space-y-5 max-w-[1400px] mx-auto">
        {/* Up-next rail: visible only when session was launched from a presentation. */}
        {returnTo && session.target_module_id && session.target_section_id && !projector.active && (
          <UpNextPreview session={session} />
        )}

        {/* Header strip */}
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-testid="back-to-list"
            className="text-slate-300 hover:text-white hover:bg-white/10"
          >
            ← Back
          </Button>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-live)]/15 text-[var(--lumyn-live)] text-[11px] font-semibold uppercase tracking-[0.14em]">
            <Flame className="w-3.5 h-3.5" />Live
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex-1 truncate" data-testid="active-quiz-title">
            {session.quiz_title}
          </h1>
          {/* Pencil — opens the same rename popover trainers see in the
              listing. Wrapped in a relative span so the popover anchors
              below it instead of off-screen. */}
          <span className="relative">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setHeaderRenaming((v) => !v); }}
              className="p-1.5 rounded-md text-slate-300 hover:text-white hover:bg-white/10 transition"
              title="Rename session"
              aria-label="Rename session"
              data-testid="host-rename-btn"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            {headerRenaming && (
              <RenamePopover
                session={session}
                onSaved={(updated) => { setHeaderRenaming(false); onUpdated(updated); }}
                onClose={() => setHeaderRenaming(false)}
              />
            )}
          </span>
          <LumynBadge variant={isRunning ? 'completed' : isEnded ? 'expired' : 'in_progress'} size="sm" />
          {/* Tiny WS connectivity dot — green=push connected, amber=polling
              fallback. Trainer-only surface; participants don't need to see
              this. Tooltip explains both states for new users. */}
          <span
            className={`inline-flex items-center justify-center w-2 h-2 rounded-full ring-2 transition-colors ${
              wsConnected
                ? 'bg-emerald-400 ring-emerald-400/30 shadow-[0_0_8px_rgba(52,211,153,0.5)]'
                : 'bg-amber-400 ring-amber-400/30 animate-pulse'
            }`}
            data-testid="ws-status-dot"
            data-ws-connected={wsConnected ? 'true' : 'false'}
            title={wsConnected
              ? 'Real-time push connected'
              : 'Real-time push unavailable — using fallback polling (the session still works)'}
            aria-label={wsConnected ? 'Real-time push connected' : 'Polling fallback active'}
          />

          {/* Cast to projector pill — fullscreen + auto-hide cursor */}
          <Button
            variant="outline"
            size="sm"
            onClick={projector.active ? projector.exit : projector.enter}
            data-testid="cast-projector-btn"
            className="border-[var(--lumyn-live)]/40 text-[var(--lumyn-live)] hover:bg-[var(--lumyn-live)]/10 hover:text-[var(--lumyn-live)] hover:border-[var(--lumyn-live)]/60"
            title={projector.active ? 'Exit projector mode (Esc)' : 'Cast to projector (fullscreen, auto-hide cursor)'}
          >
            {projector.active ? (
              <><Minimize2 className="w-3.5 h-3.5 mr-1" />Exit cast</>
            ) : (
              <><Cast className="w-3.5 h-3.5 mr-1" />Cast to projector</>
            )}
          </Button>
        </div>

        {/* Hero ref code — giant, outlined, glowing */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 sm:p-10 text-center space-y-4 relative">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
            {'Joiners go to'}
          </p>
          <a
            href={joinUrl}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-mono text-[var(--lumyn-live)] hover:underline inline-flex items-center gap-1"
            data-testid="join-url"
          >
            {joinUrl} <ExternalLink className="w-3 h-3" />
          </a>
          <p
            className="font-display font-bold tracking-[0.18em] leading-none select-all"
            style={{
              fontSize: 'clamp(4.5rem, 14vw, 11rem)',
              color: 'transparent',
              WebkitTextStroke: '2px var(--lumyn-live)',
              textShadow: '0 0 60px rgba(249,115,22,0.45), 0 0 24px rgba(249,115,22,0.25)',
            }}
            data-testid="ref-code-display"
          >
            {session.ref_code}
          </p>
          <div className="flex items-center justify-center gap-5 text-xs text-slate-300">
            <span className="inline-flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" />
              <span className="font-semibold text-white tabular-nums" data-testid="joiners-count">
                {session.participant_count}
              </span> {'joined'}
            </span>
            {(() => {
              const { label, iso } = bestSessionTimestamp(session);
              if (!iso) return null;
              return (
                <span className="inline-flex items-center gap-1.5" data-testid="host-session-timestamp">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{label} {formatSessionDateTime(iso)}</span>
                </span>
              );
            })()}
            <span className="inline-flex items-center gap-1.5">
              {session.require_participant_email ? <Mail className="w-3.5 h-3.5" /> : <MailX className="w-3.5 h-3.5" />}
              {session.require_participant_email ? 'Email required' : 'Name only'}
            </span>
            {health && (
              <span
                className={
                  'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border tabular-nums '
                  + (health.status_color === 'red'
                    ? 'border-rose-400/40 bg-rose-500/15 text-rose-200'
                    : health.status_color === 'amber'
                      ? 'border-amber-400/40 bg-amber-500/15 text-amber-200'
                      : 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200')
                }
                title={
                  `Active: ${health.participants_active} / ${health.participants_joined}\n`
                  + `Throttled this minute: ${health.participants_throttled}\n`
                  + `Near limit (≥80%): ${health.participants_warm}\n`
                  + `Peak requests/min: ${health.peak_request_count} / ${health.limit_per_minute}`
                }
                data-testid="capacity-meter"
              >
                <span
                  className={
                    'w-1.5 h-1.5 rounded-full '
                    + (health.status_color === 'red'
                      ? 'bg-rose-400 animate-pulse'
                      : health.status_color === 'amber'
                        ? 'bg-amber-400'
                        : 'bg-emerald-400')
                  }
                />
                {health.participants_throttled > 0
                  ? `${health.participants_throttled} throttled`
                  : health.participants_warm > 0
                    ? `${health.participants_warm} near limit`
                    : 'Healthy'}
              </span>
            )}
          </div>
        </div>

        {/* Pacing controls */}
        <div className="flex flex-wrap gap-2">
          {isLobby && (
            <Button onClick={() => wrap('start')} disabled={busy} data-testid="start-btn"
              className="bg-[var(--lumyn-live)] hover:bg-[var(--lumyn-live)]/90 text-white shadow-lg shadow-[var(--lumyn-live)]/20">
              <Play className="w-4 h-4 mr-1" />{'Start'}
            </Button>
          )}
          {isRunning && (
            <>
              <Button
                variant="outline"
                onClick={() => wrap('previous')}
                disabled={busy || (session.current_question_index ?? 0) <= 0}
                data-testid="previous-btn"
                className="border-white/20 text-slate-100 hover:bg-white/10"
                title="Go back to the previous question"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />{'Previous'}
              </Button>
              <Button onClick={() => wrap('advance')} disabled={busy} data-testid="advance-btn"
                className="bg-[var(--lumyn-live)] hover:bg-[var(--lumyn-live)]/90 text-white shadow-lg shadow-[var(--lumyn-live)]/20">
                {'Next question →'}
              </Button>
            </>
          )}
          {(isLobby || isRunning) && (
            <Button variant="outline" onClick={() => wrap('end')} disabled={busy} data-testid="end-btn"
              className="border-white/20 text-slate-100 hover:bg-white/10">
              {'End now'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={handleDelete}
            className="text-rose-400 hover:bg-rose-500/15 hover:text-rose-300" data-testid="delete-btn">
            <Trash2 className="w-4 h-4 mr-1" />{'Delete'}
          </Button>
        </div>

        {/* Ended session: reports */}
        {isEnded && (
          <Card className="border-[var(--lumyn-live)]/30 bg-white/[0.03] text-slate-100" data-testid="ended-actions">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-slate-100">
                <Download className="w-4 h-4" />Session ended — downloads
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {returnTo && (
                <Button
                  size="sm"
                  onClick={() => navigate(returnTo)}
                  data-testid="resume-presentation-btn"
                  className="bg-[var(--lumyn-content)] hover:bg-[var(--lumyn-content)]/90 text-white shadow-sm"
                >
                  <Play className="w-4 h-4 mr-1" />Resume presentation
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              <a href={reportPdfUrl} target="_blank" rel="noreferrer" data-testid="download-report-pdf">
                <Button variant="outline" size="sm" className="border-white/20 text-slate-100 hover:bg-white/10">
                  <FileText className="w-4 h-4 mr-1" />Report (PDF)
                </Button>
              </a>
              <a href={reportXlsxUrl} target="_blank" rel="noreferrer" data-testid="download-report-xlsx">
                <Button variant="outline" size="sm" className="border-white/20 text-slate-100 hover:bg-white/10">
                  <FileSpreadsheet className="w-4 h-4 mr-1" />Report (Excel)
                </Button>
              </a>
            </CardContent>
          </Card>
        )}

        {/* Ended session: integrity summary */}
        {isEnded && <IntegritySummaryCard sessionId={session.session_id} />}

        {/* Current-question peek */}
        {isRunning && currentQ && (
          <Card className="bg-white/[0.03] border-white/10 text-slate-100" data-testid="current-question-peek">
            <CardHeader className="pb-2">
              <CardTitle className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
                Now: question {session.current_question_index + 1} of {session.total_questions}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-lg sm:text-xl font-semibold leading-snug">{currentQ.question_text}</p>
              {(session.current_fast_correct?.length || 0) > 0 && (
                <div
                  className="flex items-center gap-2 flex-wrap rounded-lg bg-[var(--lumyn-live)]/8 ring-1 ring-[var(--lumyn-live)]/25 px-3 py-2"
                  data-testid="fast-correct-ticker"
                >
                  <Flame className="w-4 h-4 text-[var(--lumyn-live)] animate-pulse-glow flex-shrink-0" />
                  <span className="text-[11px] uppercase tracking-[0.16em] text-[var(--lumyn-live)] font-semibold">
                    First correct
                  </span>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {session.current_fast_correct.map((p) => {
                      const rankLabel = ['1st', '2nd', '3rd', '4th', '5th'][p.rank - 1] || `${p.rank}th`;
                      return (
                        <span
                          key={p.participant_id}
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${
                            p.rank === 1
                              ? 'bg-amber-400/20 text-amber-100 ring-1 ring-amber-400/40'
                              : 'bg-white/10 text-slate-200'
                          }`}
                          data-testid={`fast-correct-row-${p.rank}`}
                        >
                          <span className="font-mono text-[10px] opacity-80">{rankLabel}</span>
                          <span className="font-medium">{p.display_name}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {currentDist && <DistributionBars distribution={currentDist} />}
            </CardContent>
          </Card>
        )}

        {/* Moderation: participants with suspicion flags + remove/lock */}
        <div className="flex items-center justify-end">
          <Button
            variant="outline" size="sm"
            className="h-8 text-xs"
            onClick={async () => {
              try {
                await post(`/live/sessions/${session.session_id}/lock`, { locked: !session.locked });
                onUpdated({ ...session, locked: !session.locked });
                toast.success(!session.locked ? 'Session locked — no new joins.' : 'Session unlocked.');
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Lock toggle failed');
              }
            }}
            data-testid="lock-session-btn"
          >
            {session.locked
              ? <><LockOpen className="w-3.5 h-3.5 mr-1" />Unlock joins</>
              : <><Lock className="w-3.5 h-3.5 mr-1" />Lock joins</>}
          </Button>
        </div>
        <ParticipantModerationPanel session={session} onUpdated={onUpdated} />

        {/* Two-column lower area: leaderboard + per-question summary */}
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="bg-white/[0.03] border-white/10 text-slate-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-slate-100">
                <Trophy className="w-4 h-4 text-amber-400" />Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5" data-testid="leaderboard">
              {(session.leaderboard || []).length === 0 && (
                <p className="text-xs text-slate-400 italic">No participants yet</p>
              )}
              {(session.leaderboard || []).map((p, i) => {
                const totalQs = session.total_questions || 1;
                const pctCorrect = Math.round((p.answered_count / totalQs) * 100);
                const rankCls =
                  i === 0 ? 'text-amber-400 font-bold' :
                  i === 1 ? 'text-slate-300 font-semibold' :
                  i === 2 ? 'text-orange-300/80 font-semibold' :
                  'text-slate-500';
                return (
                  <div
                    key={p.participant_id}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${
                      i === 0 ? 'bg-amber-400/10 ring-1 ring-amber-400/30' : 'bg-white/[0.04] hover:bg-white/[0.08]'
                    }`}
                    data-testid={`lb-row-${i}`}
                  >
                    <span className={`font-mono text-xs w-5 ${rankCls}`}>{i + 1}</span>
                    <span className="flex-1 truncate">{p.display_name}</span>
                    <span className="text-xs text-slate-400 tabular-nums">
                      {p.answered_count}/{totalQs} · {pctCorrect}%
                    </span>
                    <span className="font-semibold tabular-nums">{Math.round(p.score)}</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card className="bg-white/[0.03] border-white/10 text-slate-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-100">All questions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="all-distributions">
              {allDist.length === 0 && (
                <p className="text-xs text-slate-400 italic">
                  Distribution appears once participants start answering.
                </p>
              )}
              {allDist.map((d) => <DistributionBars key={d.question_index} distribution={d} />)}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Game-show bottom strip — only while casting to projector */}
      {projector.active && <CastProgressBar session={session} />}
    </div>
  );
}
